import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '/', active: true },
    { name: 'About Us', href: '/#about' },
    { name: 'Services', href: '/#services' },
    { name: 'Quality', href: '/#quality' },
    { name: 'Contact', href: '/contact' }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-[#0B1C2C] shadow-2xl shadow-[#D4AF37]/10 border-b border-[#D4AF37]/20 backdrop-blur-xl' : 'bg-[#0B1C2C]/98 backdrop-blur-md'
      }`}
    >
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* Turbowing Logo - transparent PNG, sits cleanly on the navy header */}
          <a href="/" className="group cursor-pointer relative flex items-center" data-testid="header-logo">
            <img
              src="/turbowing-logo-clean.png"
              alt="Turbowing Aviation"
              width="240"
              height="96"
              loading="eager"
              fetchpriority="high"
              decoding="sync"
              onError={(e) => {
                if (e.currentTarget.src.indexOf('turbowing-logo.jpg') === -1) {
                  e.currentTarget.src = '/turbowing-logo.jpg';
                }
              }}
              className="h-32 sm:h-32 md:h-40 w-auto object-contain transform group-hover:scale-105 transition-transform duration-500"
              style={{
                filter: 'drop-shadow(0 2px 12px rgba(212, 175, 55, 0.25))'
              }}
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`relative group cursor-pointer text-sm font-medium uppercase tracking-wide transition-all duration-300 ${
                  link.active ? 'text-[#D4AF37]' : 'text-white/90 hover:text-[#D4AF37]'
                }`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1.5 left-0 h-0.5 bg-[#D4AF37] shadow-[0_0_10px_rgba(212,175,55,0.7)] transition-all duration-500 ${
                    link.active ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                ></span>
              </a>
            ))}
            <a href="/contact" data-testid="nav-cta-quote">
              <Button className="bg-transparent hover:bg-[#D4AF37]/10 text-[#D4AF37] font-bold px-6 py-2.5 rounded-md border-2 border-[#D4AF37] transition-all duration-300 hover:shadow-[0_0_25px_rgba(212,175,55,0.45)] hover:-translate-y-0.5 cursor-pointer uppercase tracking-wide text-sm">
                Request a Quote
              </Button>
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/10">
            <nav className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-[#C3C0C0] hover:text-[#EFBF04] transition-colors duration-300 text-sm font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <a href="/contact">
                <Button className="bg-[#D4AF37] hover:bg-[#FFD700] text-[#0B1C2C] font-bold w-full cursor-pointer shadow-[0_0_25px_rgba(212,175,55,0.4)] uppercase tracking-wide">
                  Request a Quote
                </Button>
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
